import React from 'react';

import {useIntl} from 'react-intl';

import {Link as RouterLink, Outlet, useLocation} from 'react-router-dom';

import {useDocumentTitle} from 'usehooks-ts';
import {Box, Collapse, styled, useTheme} from "@mui/material";

import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Drawer from '@mui/material/Drawer';
import {NAV_LINK_TYPE, NAV_LINKS} from "@/config/routes.tsx";
import {ExpandLess, ExpandMore} from "@mui/icons-material";
import Page from "@/components/page.tsx";
import LogoutButton from "@/auth/logout-button";

interface Props {
  children?: React.ReactNode;
}

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      },
    },
  ],
}));

// AppBar spans the full viewport width at all times and sits above the
// Drawer (z-index bump). Previously it shrank and shifted right when the
// drawer opened — the persistent-drawer MUI example — which left a white
// gap above the open drawer. Keeping it full width matches the typical
// SaaS dashboard look and removes the gap.
const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));


const NavItem: React.FC<{ link: NAV_LINK_TYPE; level?: number }> = ({ link, level = 0 }) => {
  const location = useLocation();
  
  const hasChildren = link.children && link.children.length > 0;
  const isActive = location.pathname === link.to;
  
  const isChildActive = hasChildren
    ? link.children!.some(child => location.pathname.startsWith(child.to))
    : false;

  const [open, setOpen] = React.useState(() => isChildActive);
  
  const selected = !hasChildren && isActive;
  
  const handleToggle = () => {
    setOpen((prev) => !prev);
  };
  
  return (
    <>
      <ListItemButton
        component={!hasChildren ? RouterLink : 'div'}
        to={!hasChildren ? link.to : undefined}
        onClick={hasChildren ? handleToggle : undefined}
        selected={selected}
        sx={{ pl: 2 + level * 2 }}
      >
        <ListItemIcon>{link.icon}</ListItemIcon>
        <ListItemText primary={link.label} />
        {hasChildren && (open ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
      
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {link.children!.map((child) => (
              <NavItem key={child.to} link={child} level={level + 1} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export default function MainLayout({children}: Props) {
  const {formatMessage} = useIntl();
  
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  
  const handleDrawerOpen = () => {
    setOpen(true);
  };
  
  const handleDrawerClose = () => {
    setOpen(false);
  };
  
  useDocumentTitle(formatMessage({id: 'application.title'}));
  
  // return (
  //   <>
  //     <Header />
  //     {children || <Outlet />}
  //   </>
  // );
  
  // Single drawer toggle now lives in the AppBar so it stays visible regardless
  // of drawer state. The previous design hid the open-icon when the drawer
  // was open and placed the close-icon inside the drawer — but with the AppBar
  // now full-width and z-index-above-drawer the drawer-side button would have
  // been visually obscured.
  const isLtr = theme.direction === 'ltr';
  const collapseIcon = isLtr ? <ChevronLeftIcon /> : <ChevronRightIcon />;
  const toggleDrawer = open ? handleDrawerClose : handleDrawerOpen;
  const toggleLabel = open ? 'close drawer' : 'open drawer';
  const toggleIcon = open ? collapseIcon : <MenuIcon />;

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label={toggleLabel}
            onClick={toggleDrawer}
            edge="start"
            sx={{ mr: 2 }}
          >
            {toggleIcon}
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <LogoutButton />
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        {/* Empty toolbar-sized spacer so the nav list starts below the AppBar. */}
        <DrawerHeader />

        <Divider />
        
        <List component="nav">
          {NAV_LINKS.map((link) => (
            <NavItem key={link.to} link={link} />
          ))}
        </List>
      </Drawer>
      
      <Main open={open}>
        <DrawerHeader />
        
        <Page>
          {children || <Outlet />}
        </Page>
      </Main>
    </Box>
  );
}
